import React, { useEffect, useRef } from 'react';
import { CloseButton } from './CloseButton';
import { suspendPhaserKeyboard } from '../../utils/phaser-keyboard.js';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  title?: string;
  showCloseButton?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
  maxHeight?: string;
}

export const Modal: React.FC<ModalProps> = ({ 
  isOpen, 
  onClose, 
  children, 
  className = '',
  title,
  showCloseButton = true,
  size = 'lg',
  maxHeight = '90vh'
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Size mapping for modal widths
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md', 
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl'
  };

  // Focus management and Phaser input handling while the modal is open.
  //
  // The keyboard is taken with a `modal` hold (`utils/phaser-keyboard.ts`)
  // and given back when the modal closes or unmounts. Closing a modal used to
  // force the keyboard on, which re-enabled movement under full-screen video
  // or a focused field that still needed it off (#123).
  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    // Disable Phaser pointer input, and hold the keyboard
    const game = (window as any).phaserGame;
    if (game && game.input) {
      game.input.enabled = false;
    }
    const releaseKeyboard = suspendPhaserKeyboard(game, 'modal');

    // Blur any active element (like Phaser canvas) and focus the modal
    (document.activeElement as HTMLElement)?.blur?.();

    // Focus the first focusable element in the modal
    const focusableElements = modalRef.current.querySelectorAll(
      'input, textarea, button, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length > 0) {
      (focusableElements[0] as HTMLElement).focus();
    } else {
      // Focus the modal container if no focusable elements
      modalRef.current.focus();
    }

    return () => {
      // Re-enable Phaser pointer input; the keyboard comes back only if
      // nothing else holds it
      if (game && game.input) {
        game.input.enabled = true;
      }
      releaseKeyboard();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[9999] p-4" onClick={handleOverlayClick}>
      <div 
        ref={modalRef}
        className={`bg-game-dark border border-game-medium/50 rounded-lg shadow-2xl shadow-game-medium/20 ${sizeClasses[size]} w-full overflow-hidden flex flex-col ${className}`}
        style={{ maxHeight }}
        onClick={(e) => e.stopPropagation()}
        tabIndex={-1}
      >
        {(title || showCloseButton) && (
          <div className="flex items-center justify-between p-6 border-b border-game-medium/30">
            {title && <h2 className="text-2xl font-bold text-white">{title}</h2>}
            {showCloseButton && (
              <CloseButton onClick={onClose} />
            )}
          </div>
        )}
        <div className="flex-1 overflow-y-auto p-6">
          {children}
        </div>
      </div>
    </div>
  );
};